import { lazy, Suspense } from "react";
import { Switch, Route, Router as WouterRouter } from "wouter";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Layout } from "@/components/layout";
import { SampleRequestProvider } from "@/components/marketing/SampleRequestDialog";

// ─────────────────────────────────────────────────────────────────────────────
// RECONSTRUCCION (2026-08-11) — rutas con React.lazy.
//
// El snapshot de Replit (mayo) importaba las 9 paginas de forma estatica, asi que
// el build salia como un unico bundle de 544 kB. El build de produccion que se
// recupero de la imagen tenia un chunk por ruta (home, discovery, lists,
// list-detail, login, signup, verify-email, pricing, why-leadsweet, not-found),
// asi que entre mayo y junio se paso a carga diferida. Se restaura aqui: la
// primera carga baja de 544 kB a ~300 kB, que es lo que pesaba el index de
// produccion.
//
// Los nombres de fichero de cada chunk los deriva Vite del modulo, por eso
// coinciden con los del build original.
// ─────────────────────────────────────────────────────────────────────────────
const Home = lazy(() => import("@/pages/home"));
const Login = lazy(() => import("@/pages/login"));
const Signup = lazy(() => import("@/pages/signup"));
const VerifyEmail = lazy(() => import("@/pages/verify-email"));
const ResetPassword = lazy(() => import("@/pages/reset-password"));
const Pricing = lazy(() => import("@/pages/pricing"));
const Discovery = lazy(() => import("@/pages/discovery"));
const Lists = lazy(() => import("@/pages/lists"));
const MyContacts = lazy(() => import("@/pages/my-contacts"));
const ListDetail = lazy(() => import("@/pages/list-detail"));
const WhyLeadSweet = lazy(() => import("@/pages/why-leadsweet"));
const Notes = lazy(() => import("@/pages/notes"));
const Pipeline = lazy(() => import("@/pages/pipeline"));
const PipelineLimits = lazy(() => import("@/pages/pipeline-limits"));
const ImportarSmartscout = lazy(() => import("@/pages/importar-smartscout"));
const NotFound = lazy(() => import("@/pages/not-found"));

const queryClient = new QueryClient();

function Router() {
  return (
    <Switch>
      <Route path="/">
        <Layout><Home /></Layout>
      </Route>
      <Route path="/login">
        <Layout><Login /></Layout>
      </Route>
      <Route path="/signup">
        <Layout><Signup /></Layout>
      </Route>
      {/* El enlace del email de verificacion apunta a /verify-email?token=...
          (lo construye routes/auth.ts). No renombrar: invalidaria los correos
          ya enviados. */}
      <Route path="/verify-email">
        <Layout><VerifyEmail /></Layout>
      </Route>
      {/* Igual que /verify-email: el enlace del correo apunta a
          /reset-password?token=... (routes/auth.ts). Sin token la misma pagina
          enseña el formulario para pedirlo. No renombrar. */}
      <Route path="/reset-password">
        <Layout><ResetPassword /></Layout>
      </Route>
      <Route path="/pricing">
        <Layout><Pricing /></Layout>
      </Route>
      <Route path="/why-leadsweet">
        <Layout><WhyLeadSweet /></Layout>
      </Route>
      <Route path="/discovery">
        <Layout><Discovery /></Layout>
      </Route>
      <Route path="/lists">
        <Layout><Lists /></Layout>
      </Route>
      <Route path="/lists/:id">
        <Layout><ListDetail /></Layout>
      </Route>
      <Route path="/contacts">
        <Layout><MyContacts /></Layout>
      </Route>
      {/* Bandeja de las notas ancladas. La ruta existe siempre; el API responde
          404 a quien no haya entrado por Amazify y la pagina lo explica. */}
      <Route path="/notes">
        <Layout><Notes /></Layout>
      </Route>
      {/* Pipeline de enriquecimiento y sus limites de gasto. Las rutas existen
          siempre; la API responde 404 a quien no haya entrado por "Login with
          Amazify", asi que sin esa sesion las paginas no traen datos y lo
          explican. Mismo criterio que las notas ancladas.

          /pipeline/limits va ANTES que /pipeline: wouter casa en orden y una
          ruta sin comodin no atraparia la hija, pero dejarlo explicito evita
          que un `/pipeline/:algo` futuro se coma esta sin que nadie lo note. */}
      <Route path="/pipeline/limits">
        <Layout><PipelineLimits /></Layout>
      </Route>
      <Route path="/pipeline/import">
        <Layout><ImportarSmartscout /></Layout>
      </Route>
      <Route path="/pipeline">
        <Layout><Pipeline /></Layout>
      </Route>
      <Route>
        <Layout><NotFound /></Layout>
      </Route>
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        {/* El limite de errores envuelve al router, igual que en la version
            desplegada: sin el, un error de render en cualquier pagina deja la
            pantalla en blanco. Va DENTRO de los proveedores para que la
            pantalla de recuperacion tenga estilos y tooltips. */}
        <ErrorBoundary>
        {/* El formulario de "Request a custom sample" se monta UNA vez, por
            encima del router, para que cualquier CTA de cualquier pagina pueda
            abrirlo con useSampleRequest(). */}
        <SampleRequestProvider>
        <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
          {/* Con rutas lazy hace falta un limite de Suspense. Se deja en blanco
              a proposito: los chunks son pequenos y en red local se resuelven en
              milisegundos, asi que un spinner solo produciria un parpadeo. */}
          <Suspense fallback={null}>
            <Router />
          </Suspense>
        </WouterRouter>
        </SampleRequestProvider>
        </ErrorBoundary>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
